import { useMemo } from 'react'
import { TrendingUp, TrendingDown, Wallet, PiggyBank } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { useBalances, useCardMonthFatura } from '@/hooks/useAccounts'
import { useTransactions, usePendingExpensesUntil } from '@/hooks/useTransactions'
import { useRecurringRules } from '@/hooks/useRecurring'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import { useHousehold } from '@/context/HouseholdContext'
import { monthOccurrenceStatus } from '@/lib/recurring'
import { dashboardProjection, faturaMonthOf } from '@/lib/finance'
import { formatCurrency } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { AccountsOverview } from '@/components/dashboard/AccountsOverview'
import { useT } from '@/i18n/I18nProvider'

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function monthRange() {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return { from: iso(first), to: iso(last) }
}

// A busca vai 2 meses PARA TRÁS de propósito: uma compra de cartão feita antes do
// fechamento do mês passado só é cobrada na fatura deste mês, então o caixa dela é
// deste mês mesmo tendo data do mês passado. O recorte final é por `faturaMonthOf`.
function fetchRange() {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth() - 2, 1)
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return { from: iso(first), to: iso(last) }
}

export function DashboardPage() {
  const { t } = useT()
  const { baseCurrency } = useHousehold()
  const { data: balances } = useBalances()
  // Régua da FATURA: a dívida do mês é o que VENCE no mês. Uma compra feita depois
  // do fechamento só é paga na fatura seguinte — ela não pode derrubar o saldo
  // previsto deste mês. Sem fatura marcada isto é idêntico à régua por data.
  const { data: cardFatura } = useCardMonthFatura(undefined, undefined, true)
  const { data: rates } = useCurrentRates()
  const range = useMemo(monthRange, [])
  const { data: fetchedTxs, isPending: txsPending } = useTransactions(useMemo(fetchRange, []))
  // Lançamentos cujo CAIXA é deste mês (ver fetchRange). Sem fatura marcada é o
  // mês da própria data — idêntico ao que o app sempre fez.
  const monthTxs = useMemo(
    () => (fetchedTxs ?? []).filter((t) => faturaMonthOf(t) === range.from.slice(0, 7)),
    [fetchedTxs, range.from],
  )
  const { data: rules } = useRecurringRules()
  // Pendências em aberto (não pagas) até o fim do mês — inclui atrasadas de meses anteriores.
  const { data: pendingOpen } = usePendingExpensesUntil(range.to)

  // Receitas/despesas do mês (base) — inclui pago E pendente. Transferências (par
  // com transfer_group_id) NÃO são receita/despesa e ficam de fora.
  const income = (monthTxs ?? [])
    .filter((t) => t.kind === 'income' && !t.transfer_group_id && t.amount_base != null)
    .reduce((s, t) => s + Number(t.amount_base), 0)
  const expense = (monthTxs ?? [])
    .filter((t) => t.kind === 'expense' && !t.transfer_group_id && t.amount_base != null)
    .reduce((s, t) => s + Number(t.amount_base), 0)
  const incomePaid = (monthTxs ?? [])
    .filter((t) => t.kind === 'income' && !t.transfer_group_id && t.status === 'paid' && t.amount_base != null)
    .reduce((s, t) => s + Number(t.amount_base), 0)
  const expensePaid = (monthTxs ?? [])
    .filter((t) => t.kind === 'expense' && !t.transfer_group_id && t.status === 'paid' && t.amount_base != null)
    .reduce((s, t) => s + Number(t.amount_base), 0)

  // Cartões são dívida: ficam fora do saldo das contas; a fatura do mês entra nas dívidas.
  const cardIds = useMemo(
    () => new Set((balances ?? []).filter((b) => b.account.type === 'cartao').map((b) => b.account.id)),
    [balances],
  )

  // Saldo das contas (sem cartões), consolidado na moeda base.
  let saldoContas = 0
  let hasMissingRate = false
  for (const b of balances ?? []) {
    if (b.account.type === 'cartao') continue
    const conv = convertWithRates(b.balance, b.account.currency, rates)
    if (conv == null) hasMissingRate = true
    else saldoContas += conv
  }

  // Dívidas do mês (inclui cartões): a FATURA EM ABERTO do mês nos cartões (já
  // líquida de pagamentos — se a fatura foi paga, o valor sai daqui e o dinheiro
  // já saiu da conta) + TODAS as despesas pendentes fora de cartão até o fim do mês.
  let dividaCartoesMes = 0
  for (const b of balances ?? []) {
    if (b.account.type !== 'cartao') continue
    // COM SINAL: dívida soma; crédito no cartão (estorno/pagamento a mais) é
    // dinheiro a seu favor e ABATE — antes era descartado e sumia do saldo.
    const debt = cardFatura?.get(b.account.id) ?? 0
    const conv = convertWithRates(debt, b.account.currency, rates)
    if (conv == null) hasMissingRate = true
    else dividaCartoesMes += conv
  }
  const pendentesNaoCartao = (pendingOpen ?? [])
    .filter((t) => t.amount_base != null && !cardIds.has(t.account_id))
    .reduce((s, t) => s + Number(t.amount_base), 0)
  // Recorrentes do mês AINDA NÃO lançados (aluguel, impostos, assinaturas…):
  // são obrigações/receitas do mês que ainda não viraram transação. Convertidos
  // pela taxa atual (a base histórica não existe pois não há lançamento ainda).
  const recorrentesPend = useMemo(() => {
    const now = new Date()
    const y = now.getFullYear()
    const m0 = now.getMonth()
    let expense = 0
    let income = 0
    let missing = false
    for (const r of rules ?? []) {
      for (const o of monthOccurrenceStatus(r, monthTxs ?? [], y, m0)) {
        if (o.included) continue
        const conv = convertWithRates(r.template.amount, r.template.currency, rates)
        if (conv == null) {
          missing = true
          continue
        }
        if (r.template.kind === 'expense') expense += conv
        else if (r.template.kind === 'income') income += conv
      }
    }
    return { expense, income, missing }
  }, [rules, monthTxs, rates])

  // Receitas pendentes que vão CAIR NA CONTA. Exclui cartão: receita em cartão é
  // estorno — não entra na conta, apenas reduz a fatura (que já vem líquida dela).
  const receitasLancadas = (monthTxs ?? [])
    .filter(
      (t) =>
        t.kind === 'income' &&
        t.status === 'pending' &&
        !t.transfer_group_id &&
        t.amount_base != null &&
        !cardIds.has(t.account_id),
    )
    .reduce((s, t) => s + Number(t.amount_base), 0)
  // Projeção do fim do mês (finance.dashboardProjection — mesma fórmula testada).
  const { receitasAReceber, saldoLiquido } = dashboardProjection({
    saldoContas,
    cardFaturaAberta: dividaCartoesMes,
    pendentesNaoCartao,
    recorrentesDespesa: recorrentesPend.expense,
    receitasLancadas,
    recorrentesReceita: recorrentesPend.income,
  })
  if (recorrentesPend.missing) hasMissingRate = true

  // Só mostra números com TODAS as fontes carregadas. Renderizar com dados pela
  // metade (ex.: saldo já veio, fatura dos cartões não) fazia o valor piscar
  // absurdo — positivo/negativo — antes de estabilizar.
  const ready = Boolean(balances && cardFatura && rates && !txsPending && fetchedTxs !== undefined && rules && pendingOpen)

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('dash.title')}
        titleClassName="text-3xl"
        subtitle={t('dash.subtitle', { currency: baseCurrency })}
      />

      {!ready && (
        <Card className="border-primary/30">
          <CardContent className="text-muted-foreground p-6 text-center text-sm">{t('common.loading')}</CardContent>
        </Card>
      )}

      {/* Saldo previsto no fim do mês (fluxo) */}
      {ready && (
      <Card className="border-primary/30">
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <div className="text-muted-foreground text-sm">{t('dash.expectedBalance')}</div>
            <div
              className={cn('text-3xl font-semibold tabular-nums', saldoLiquido < 0 ? 'text-destructive' : undefined)}
            >
              {formatCurrency(saldoLiquido, baseCurrency)}
            </div>
            <div className="text-muted-foreground text-xs">
              {t('dash.breakdownAccounts', { value: formatCurrency(saldoContas, baseCurrency) })}
              {receitasAReceber > 0 &&
                t('dash.breakdownReceivable', { value: formatCurrency(receitasAReceber, baseCurrency) })}
              {t('dash.breakdownDebts', {
                value: formatCurrency(dividaCartoesMes + pendentesNaoCartao, baseCurrency),
              })}
              {recorrentesPend.expense > 0 &&
                t('dash.breakdownRecurring', { value: formatCurrency(recorrentesPend.expense, baseCurrency) })}
              {hasMissingRate && ' *'}
            </div>
          </div>
          <PiggyBank className="text-primary size-6" />
        </CardContent>
      </Card>
      )}

      {/* Receitas/despesas do mês (total = pago + pendente) */}
      {ready && (
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          title={t('dash.incomeMonth')}
          value={formatCurrency(income, baseCurrency)}
          hint={t('dash.received', { value: formatCurrency(incomePaid, baseCurrency) })}
          icon={<TrendingUp className="text-success size-5" />}
        />
        <StatCard
          title={t('dash.expenseMonth')}
          value={formatCurrency(expense, baseCurrency)}
          hint={t('dash.paid', { value: formatCurrency(expensePaid, baseCurrency) })}
          icon={<TrendingDown className="text-destructive size-5" />}
        />
        <StatCard
          title={t('dash.resultMonth')}
          value={formatCurrency(income - expense, baseCurrency)}
          icon={<Wallet className="size-5" />}
        />
      </div>
      )}

      {/* Resumo de contas agrupado (estilo Lunch Money) */}
      <AccountsOverview />
    </div>
  )
}

function StatCard({
  title,
  value,
  hint,
  icon,
}: {
  title: string
  value: string
  hint?: string
  icon: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <div className="text-muted-foreground text-sm">{title}</div>
          <div className="text-2xl font-semibold tabular-nums">{value}</div>
          {hint && <div className="text-muted-foreground text-xs">{hint}</div>}
        </div>
        {icon}
      </CardContent>
    </Card>
  )
}
